import type { FimSubjectRecord } from 'features/fim/types'
import { useRolodexSignedURLs } from 'features/rolodex'
import { useMemo, useSyncExternalStore } from 'react'

import { subjectPhotoEntries, toSubjectPhotos } from './photoEntry'
import { photoUploadStore } from './photoUploadStore'
import {
  PHOTO_FILE_PATH,
  PHOTO_THUMBNAIL_PATH,
  type SubjectPhoto,
} from './types'
import { useSiteAttachedPhotos } from './useSiteAttachedPhotos'

export function useSubjectPhotos(
  siteID: string,
  subject: FimSubjectRecord
): {
  photos: SubjectPhoto[]
  truncated: boolean
  refresh: () => Promise<unknown>
} {
  const {
    photos: sitePhotos,
    truncated,
    refresh,
  } = useSiteAttachedPhotos(siteID)

  const entries = useMemo(
    () => subjectPhotoEntries(subject, sitePhotos),
    [sitePhotos, subject]
  )

  const signedUrlQueries = useMemo(
    () =>
      entries.flatMap((entry) => [
        { entry_id: entry.id, path: PHOTO_THUMBNAIL_PATH },
        { entry_id: entry.id, path: PHOTO_FILE_PATH },
      ]),
    [entries]
  )
  const { results: signedUrls } = useRolodexSignedURLs(
    signedUrlQueries.length > 0 ? signedUrlQueries : undefined
  )

  const queuedPhotos = useSyncExternalStore(
    photoUploadStore.subscribe,
    photoUploadStore.getItems
  )
  const photos = useMemo(
    () =>
      toSubjectPhotos(subject, entries, signedUrls).map((photo) => {
        const previewUrl = queuedPhotos.find(
          (item) =>
            item.siteId === siteID &&
            item.subjectUid === subject.subjectUid &&
            item.photoId === photo.entry.id
        )?.previewUrl
        return {
          ...photo,
          thumbnailUrl: photo.thumbnailUrl ?? photo.imageUrl ?? previewUrl,
          imageUrl: photo.imageUrl ?? previewUrl,
        }
      }),
    [entries, signedUrls, subject, queuedPhotos, siteID]
  )

  return { photos, truncated, refresh }
}
